import React from 'react';
import { SearchableSelect as BaseSearchableSelect } from './SearchableSelect';
import { useFormContext } from 'react-hook-form';

interface SelectOption {
  value: string | number;
  label: string;
}

interface SelectProps extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'onChange' | 'value'> {
  label?: string;
  error?: string;
  required?: boolean;
  options: SelectOption[];
  placeholder?: string;
  icon?: React.ReactNode;
  searchable?: boolean;
  value?: string | number;
  onChange?: (value: string | number) => void | ((e: React.ChangeEvent<HTMLSelectElement>) => void);
}

export const Select: React.FC<SelectProps> = ({
  label,
  error,
  required = false,
  options,
  placeholder,
  icon,
  className = '',
  searchable = true,
  value: propsValue,
  onChange: propsOnChange,
  ...restProps
}) => {
  let formContext: ReturnType<typeof useFormContext> | null = null;
  try {
    formContext = useFormContext();
  } catch {
    // Not inside a form context, that's okay
  }

  const fieldName = restProps.name;

  // If used with react-hook-form, get value and onChange from form context
  const formValue = fieldName && formContext ? formContext.watch(fieldName) : undefined;
  const formOnChange = fieldName && formContext
    ? (value: string | number) => {
        formContext!.setValue(fieldName, value, { shouldValidate: true, shouldDirty: true });
      }
    : undefined;

  // Use form value/onChange if available, otherwise use props
  const value = formValue !== undefined ? formValue : propsValue;
  const onChange = formOnChange || (propsOnChange as (value: string | number) => void | undefined);

  return (
    <BaseSearchableSelect
      label={label}
      error={error}
      required={required}
      options={options}
      placeholder={placeholder}
      icon={icon}
      className={className}
      value={value}
      onChange={onChange as (value: string | number) => void}
      searchable={searchable}
      disabled={restProps.disabled}
      name={fieldName}
    />
  );
};

